/** Descriptive workflow shortcuts that complement the compact WorkspaceDock. */
import { Box, Divider, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FolderIcon from "@mui/icons-material/Folder";
import IosShareIcon from "@mui/icons-material/IosShare";
import ScienceIcon from "@mui/icons-material/Science";
import type { ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import { GlassSurface } from "../../../layouts/components/GlassSurface";
import { reducedMotionMediaQuery } from "../../../theme/motionTokens";

interface QuickAction {
	title: string;
	description: string;
	to: string;
	icon: ReactNode;
}

const ACTIONS: QuickAction[] = [
	{
		title: "Review projects",
		description: "Create or open the agronomic context that owns samples and tests.",
		to: "/projects",
		icon: <FolderIcon />,
	},
	{
		title: "Start soil analysis",
		description: "Open the supported route-based soil-test workflow.",
		to: "/soil-tests/new",
		icon: <ScienceIcon />,
	},
	{
		title: "Inspect FlahaCALC export",
		description: "Review the current read-only projection of authoritative soil outputs.",
		to: "/flahacalc-export",
		icon: <IosShareIcon />,
	},
];

export function QuickActionsPanel() {
	return (
		<GlassSurface
			component="section"
			aria-labelledby="quick-actions-title"
			intensity="standard"
			depth="raised"
		>
			<Box sx={{ p: 2.5 }}>
				<Typography id="quick-actions-title" component="h2" variant="h6">
					Workflow shortcuts
				</Typography>
				<Typography variant="caption" sx={{ color: "workspace.textMuted" }}>
					Detailed next steps complement the dock’s frequent-route launchers.
				</Typography>
			</Box>
			<Divider sx={{ borderColor: "workspace.border" }} />
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: {
						xs: "minmax(0, 1fr)",
						md: "repeat(3, minmax(0, 1fr))",
					},
					gap: 1.5,
					p: 2.5,
				}}
			>
				{ACTIONS.map((action) => (
					<Box
						key={action.to}
						component={RouterLink}
						to={action.to}
						sx={(theme) => ({
							display: "flex",
							minWidth: 0,
							minHeight: 118,
							p: 2,
							borderRadius: 1.5,
							border: `1px solid ${theme.palette.workspace.border}`,
							color: "inherit",
							textDecoration: "none",
							transition: theme.motion.transition.surface,
							"&:hover, &:focus-visible": {
								transform: "translateY(-2px)",
								borderColor: "workspace.green",
								backgroundColor: "rgba(119,169,87,0.07)",
							},
							[reducedMotionMediaQuery]: {
								transition: "none",
								transform: "none",
							},
						})}
					>
						<Stack spacing={1.25} sx={{ minWidth: 0, width: "100%" }}>
							<Stack direction="row" spacing={1.25} alignItems="center">
								<Box aria-hidden="true" sx={{ display: "flex", color: "workspace.green" }}>
									{action.icon}
								</Box>
								<Typography component="h3" variant="subtitle1" sx={{ minWidth: 0, flex: 1 }}>
									{action.title}
								</Typography>
								<ArrowForwardIcon aria-hidden="true" fontSize="small" sx={{ color: "workspace.textMuted" }} />
							</Stack>
							<Typography variant="body2" sx={{ color: "workspace.textSecondary" }}>
								{action.description}
							</Typography>
						</Stack>
					</Box>
				))}
			</Box>
		</GlassSurface>
	);
}
